import type { TPlayerRegistrationResponse } from '@lucimark/shared/database/players-directory-schema'
import type { TPlayerRemoteCommand } from '@lucimark/shared/models/player-remote-control'
import type { TPlayerDiagnosticCommand } from '@lucimark/shared/models/player-remote-diagnostics'
import type { IPlayerUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { TSetScreenBrightnessWsPayload } from '@lucimark/shared/models/player-brightness'
import type { TScreenRotation } from '@lucimark/shared/models/screen-rotation'
import { PLAYER_WS_TYPE_PLAYER_UNREGISTERED } from '@lucimark/shared/constants/player-websocket'
import { setScreenBrightnessWsPayloadSchema } from '@lucimark/shared/models/player-brightness'
import { setCurrentStatusOverlayWsPayloadSchema, setTimersOverlayWsPayloadSchema } from '@lucimark/shared/models/player-overlay-ui'
import { playerRemoteCommandSchema } from '@lucimark/shared/models/player-remote-control'
import { playerDiagnosticCommandSchema } from '@lucimark/shared/models/player-remote-diagnostics'
import { showMeOverlayWsPayloadSchema } from '@lucimark/shared/models/player-show-me-overlay'
import { screenRotationSchema } from '@lucimark/shared/models/screen-rotation'

export interface IWebSocketMessageProcessor {
  parseMsg: (rawMsg: string) => Promise<void>
}

export interface IWebSocketMessageProcessorParams {
  onPlayerRegistration: (registration: TPlayerRegistrationResponse) => Promise<void> | void
  onPlayerRegistrationStatusUpdate: () => Promise<void> | void
  /** Tenant removed this screen; clear tenant state immediately (do not debounce). */
  onPlayerUnregistered: () => Promise<void> | void
  onRequestPlaybackHeartbeat: () => Promise<void> | void
  playerResetHandler: () => Promise<void>
  playerUpdateHandler: (update: IPlayerUpdateEventPayload) => Promise<void>
  screenRotationHandler: (rotation: TScreenRotation) => void
  setScreenBrightnessHandler: (payload: TSetScreenBrightnessWsPayload) => void
  showMeOverlayHandler: (payload: { title: string, durationSeconds: number }) => void
  toggleCurrentStatusOverlayHandler: () => void
  toggleTimersOverlayHandler: () => void
  setTimersOverlayHandler: (visible: boolean) => void
  setCurrentStatusOverlayHandler: (visible: boolean) => void
  audienceWebRtcSignalHandler: (payload: unknown) => void
  playerDiagnosticCommandHandler: (command: TPlayerDiagnosticCommand) => void
  playerRemoteCommandHandler: (command: TPlayerRemoteCommand) => void
}
export default class WebSocketMessageProcessor implements IWebSocketMessageProcessor {
  onPlayerRegistration: (registration: TPlayerRegistrationResponse) => Promise<void> | void
  onPlayerRegistrationStatusUpdate: () => Promise<void> | void
  onPlayerUnregistered: () => Promise<void> | void
  onRequestPlaybackHeartbeat: () => Promise<void> | void
  playerResetHandler: () => Promise<void>
  playerUpdateHandler: (update: IPlayerUpdateEventPayload) => Promise<void>
  screenRotationHandler: (rotation: TScreenRotation) => void
  setScreenBrightnessHandler: (payload: TSetScreenBrightnessWsPayload) => void
  showMeOverlayHandler: (payload: { title: string, durationSeconds: number }) => void
  toggleCurrentStatusOverlayHandler: () => void
  toggleTimersOverlayHandler: () => void
  setTimersOverlayHandler: (visible: boolean) => void
  setCurrentStatusOverlayHandler: (visible: boolean) => void
  audienceWebRtcSignalHandler: (payload: unknown) => void
  playerDiagnosticCommandHandler: (command: TPlayerDiagnosticCommand) => void
  playerRemoteCommandHandler: (command: TPlayerRemoteCommand) => void

  constructor(params: IWebSocketMessageProcessorParams) {
    const {
      onPlayerRegistration,
      onPlayerRegistrationStatusUpdate,
      onPlayerUnregistered,
      onRequestPlaybackHeartbeat,
      playerResetHandler,
      playerUpdateHandler,
      screenRotationHandler,
      setScreenBrightnessHandler,
      showMeOverlayHandler,
      toggleCurrentStatusOverlayHandler,
      toggleTimersOverlayHandler,
      setTimersOverlayHandler,
      setCurrentStatusOverlayHandler,
      audienceWebRtcSignalHandler,
      playerDiagnosticCommandHandler,
      playerRemoteCommandHandler
    } = params
    this.onPlayerRegistration = onPlayerRegistration
    this.onPlayerRegistrationStatusUpdate = onPlayerRegistrationStatusUpdate
    this.onPlayerUnregistered = onPlayerUnregistered
    this.onRequestPlaybackHeartbeat = onRequestPlaybackHeartbeat
    this.playerResetHandler = playerResetHandler
    this.playerUpdateHandler = playerUpdateHandler
    this.screenRotationHandler = screenRotationHandler
    this.setScreenBrightnessHandler = setScreenBrightnessHandler
    this.showMeOverlayHandler = showMeOverlayHandler
    this.toggleCurrentStatusOverlayHandler = toggleCurrentStatusOverlayHandler
    this.toggleTimersOverlayHandler = toggleTimersOverlayHandler
    this.setTimersOverlayHandler = setTimersOverlayHandler
    this.setCurrentStatusOverlayHandler = setCurrentStatusOverlayHandler
    this.audienceWebRtcSignalHandler = audienceWebRtcSignalHandler
    this.playerDiagnosticCommandHandler = playerDiagnosticCommandHandler
    this.playerRemoteCommandHandler = playerRemoteCommandHandler
  }

  async parseMsg(rawMsg: string) {
    let msg: object
    try {
      msg = JSON.parse(rawMsg) as object
    }
    catch (err) {
      console.warn('invalid socket message (JSON parse failed)', {
        err: err instanceof Error ? err.message : String(err),
        preview: typeof rawMsg === 'string' ? rawMsg.slice(0, 120) : rawMsg
      })
      return
    }
    if (
      typeof msg === 'object'
      && msg !== null
      && 'type' in msg
      && typeof msg.type === 'string'
      && 'payload' in msg
    ) {
      switch (msg.type) {
        case 'registration':
          await this.onPlayerRegistration(msg.payload as TPlayerRegistrationResponse)
          break
        case 'registration-status-update':
          await this.onPlayerRegistrationStatusUpdate()
          break
        case PLAYER_WS_TYPE_PLAYER_UNREGISTERED:
          await this.onPlayerUnregistered()
          break
        case 'request-playback-heartbeat':
          await this.onRequestPlaybackHeartbeat()
          break
        case 'player-update-available':
          void this.playerUpdateHandler(msg.payload as IPlayerUpdateEventPayload)
          break
        case 'reset':
          void this.playerResetHandler()
          break
        case 'set-screen-rotation': {
          const pl = msg.payload as { rotation?: unknown }
          const parsed = screenRotationSchema.safeParse(pl?.rotation)
          if (parsed.success) {
            this.screenRotationHandler(parsed.data)
          }
          break
        }
        case 'set-screen-brightness': {
          const parsed = setScreenBrightnessWsPayloadSchema.safeParse(msg.payload)
          if (parsed.success) {
            this.setScreenBrightnessHandler(parsed.data)
          }
          break
        }
        case 'show-me': {
          const parsed = showMeOverlayWsPayloadSchema.safeParse(msg.payload)
          if (parsed.success) {
            this.showMeOverlayHandler(parsed.data)
          }
          break
        }
        case 'toggle-current-status-overlay':
          this.toggleCurrentStatusOverlayHandler()
          break
        case 'toggle-timers-overlay':
          this.toggleTimersOverlayHandler()
          break
        case 'set-timers-overlay': {
          const parsed = setTimersOverlayWsPayloadSchema.safeParse(msg.payload)
          if (parsed.success) {
            this.setTimersOverlayHandler(parsed.data.visible)
          }
          break
        }
        case 'set-current-status-overlay': {
          const parsed = setCurrentStatusOverlayWsPayloadSchema.safeParse(msg.payload)
          if (parsed.success) {
            this.setCurrentStatusOverlayHandler(parsed.data.visible)
          }
          break
        }
        case 'audience-webrtc-signal':
          void this.audienceWebRtcSignalHandler(msg.payload)
          break
        case 'player-diagnostic-command': {
          const parsed = playerDiagnosticCommandSchema.safeParse(msg.payload)
          if (parsed.success) {
            void this.playerDiagnosticCommandHandler(parsed.data)
          }
          break
        }
        case 'player-remote-command': {
          const parsed = playerRemoteCommandSchema.safeParse(msg.payload)
          if (parsed.success) {
            void this.playerRemoteCommandHandler(parsed.data)
          }
          break
        }
        default:
          break
      }
    }
    else {
      console.warn('invalid socket message', rawMsg)
    }
  }
}
